import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { BookOpen, DollarSign, CheckCircle, AlertCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { Helmet } from 'react-helmet-async';

export default function Research() {
  const statistics = [
    {
      number: '100+',
      title: 'Years of Evidence',
      description: 'Global studies show home book access predicts literacy and academic success.'
    },
    {
      number: '60',
      title: 'School RCT Study',
      description: 'Largest long-term trial on book distribution and home libraries.'
    },
    {
      number: '+0.52 SD',
      title: 'Learning Gains',
      description: 'Receiving books for 5 years produced the equivalent of nearly 2 school years of learning.'
    }
  ];

  const researchStudies = [
    {
      title: 'Holley (1916)',
      content: 'Home books correlated with educational opportunity'
    },
    {
      title: 'Coleman et al. (1966)',
      content: 'Access to reading materials predicts school performance'
    },
    {
      title: 'Evans et al. (2010)',
      content: 'Children with 100+ books gain the equivalent of three additional years of schooling'
    },
    {
      title: 'Allington et al. (2010)',
      content: 'RCT showing free books improve reading scores'
    },
    {
      title: 'Kim & Quinn (2013)',
      content: 'Meta-analyses confirming book access improves outcomes for low-income students'
    },
    {
      title: 'Neuman & Celano (2001)',
      content: 'Identified "book deserts" in US cities'
    },
    {
      title: 'Sikora et al. (2019)',
      content: 'Adults raised in book-rich homes attain literacy levels similar to university graduates'
    }
  ];

  // Chart data for literacy gains with different colors
  const literacyGainData = [
    { books: '50 books', gain: 0.20, label: '+0.20', color: '#14919f' },
    { books: '100 books', gain: 0.40, label: '+0.40', color: '#2c5570' },
    { books: '130 books', gain: 0.52, label: '+0.52', color: '#fad53a' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Research (Old Version) - The Home Library Project</title>
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#2c5570] to-[#14919f] text-white py-20 bg-cover bg-top" style={{ backgroundImage: `url('/bg-research-hero.jpg')` }}>
        <div className="absolute inset-0 bg-gradient-to-r from-[#2c5570]/85 to-[#14919f]/85"></div>
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-black mb-6">
            Why Book Ownership Works
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            A century of global research shows that when children grow up with books in their home, their reading and learning improve. Books build vocabulary, background knowledge, and curiosity. Together these create the conditions children need to become strong readers.
          </p>
        </div>
      </section>

      {/* Image Section - Below Hero */}
      <section className="py-4 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 flex justify-center">
          <img 
            src="/research-image-1.jpg" 
            alt="Children reading together" 
            className="w-1/2 h-auto rounded-lg"
          />
        </div>
      </section>

      {/* Section 2: The Research Is Clear */}
      <section className="pt-8 pb-16 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <p className="text-lg leading-relaxed text-[#4a4a4a] text-center">
            Across many countries and many decades of studies, the pattern is clear. Children with few or no books begin school behind, while children surrounded by books develop stronger reading skills, richer language, and better long-term outcomes. This broad body of research shows that the size of a child's home library is one of the most consistent predictors of educational success.
          </p>
        </div>
      </section>

      {/* Section 3: Key Findings at a Glance */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {statistics.map((stat, index) => (
              <Card key={index} className="text-center border-0 shadow-lg">
                <CardContent className="p-8">
                  <div className="text-5xl font-black text-[#14919f] mb-4">
                    {stat.number}
                  </div>
                  <h3 className="text-xl font-bold text-[#2c5570] mb-3">
                    {stat.title}
                  </h3>
                  <p className="text-sm text-[#4a4a4a] leading-relaxed">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Five-Year Randomized Controlled Trial */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column with Title */}
            <div>
              <h2 className="text-4xl font-extrabold text-[#2c5570] mb-6">
                Five-Year Randomized Controlled Trial (RCT)
              </h2>
              
              <p className="text-base leading-relaxed text-[#4a4a4a] mb-6">
                A recent five-year randomized controlled trial in 60 schools demonstrated the causal impact of giving children books to build their home libraries.
              </p>
              <p className="text-base leading-relaxed text-[#4a4a4a] mb-4 font-semibold">
                Key results for children who consistently received books ("stayers"):
              </p>
              <ul className="space-y-3 text-base text-[#4a4a4a] mb-6">
                <li className="flex items-start">
                  <span className="text-[#14919f] mr-3 mt-1">•</span>
                  <span>34 books over five years produced a +0.18 SD gain in reading</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#14919f] mr-3 mt-1">•</span>
                  <span>Equivalent to 60-80% of a school year of additional learning</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#14919f] mr-3 mt-1">•</span>
                  <span>Children who moved schools still gained +0.10 SD, equal to 30-50% of a school year</span>
                </li>
              </ul>
            </div>

            {/* Right Column - Visual Chart with Multi-Color Bars */}
            <Card className="bg-white border border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#2c5570] mb-6 text-center">
                  Impact on Reading Proficiency
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={literacyGainData} margin={{ top: 20, right: 10, left: 70, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="books" 
                      tick={{ fill: '#4a4a4a', fontSize: 13 }}
                      angle={0}
                      textAnchor="middle"
                    />
                    <YAxis 
                      tick={{ fill: '#4a4a4a' }}
                      domain={[0, 0.6]}
                    />
                    {/* Separate Y-axis label as text element */}
                    <text 
                      x={15} 
                      y={120} 
                      transform="rotate(-90 15 120)" 
                      textAnchor="middle" 
                      style={{ fill: '#2c5570', fontWeight: 600, fontSize: 13 }}
                    >
                      Literacy Gain (SD)
                    </text>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '2px solid #14919f', borderRadius: '8px' }}
                      labelStyle={{ color: '#2c5570', fontWeight: 'bold' }}
                      formatter={(value) => [`${value} SD`, 'Literacy Gain']}
                    />
                    <Bar dataKey="gain" radius={[8, 8, 0, 0]}>
                      <LabelList dataKey="label" position="top" style={{ fill: '#2c5570', fontWeight: 'bold', fontSize: 14 }} />
                      {literacyGainData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-xs text-center text-[#4a4a4a] mt-4">
                  Data from 5-year RCT study across 60 schools
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Full-width paragraph */}
          <div className="mt-8">
            <p className="text-base leading-relaxed text-[#4a4a4a]">
              These findings validate the Home Library Project's design: ten books per year from birth through eighth grade, building a true home library of one hundred books by adolescence. For less than $50 per child per year, there is no other intervention that delivers this scale of impact.
            </p>
          </div>
        </div>
      </section>

      {/* Section 5: Return on Investment - Dark Background */}
      <section className="relative py-16 bg-cover bg-center" style={{ backgroundImage: `url('/bg-research-roi.jpg')` }}>
        <div className="absolute inset-0 bg-[#2c5570]/90"></div>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-extrabold text-white mb-6 text-center">
            Return on Investment
          </h2>
          
          <p className="text-lg leading-relaxed text-white/90 text-center max-w-4xl mx-auto mb-12">
            Traditional literacy interventions cost hundreds or thousands of dollars per student, while The Home Library Project delivers comparable or better outcomes for a fraction of the cost.
          </p>

          {/* Price Comparison Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* High-Cost Card 1 */}
            <Card className="bg-red-50/95 backdrop-blur-sm">
              <CardContent className="p-8 text-center">
                <div className="mb-4">
                  <AlertCircle className="w-8 h-8 text-[#b63435] mx-auto" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold text-[#2c5570] mb-3">
                  High-Dosage Tutoring
                </h3>
                <div className="text-4xl font-black text-[#b63435] mb-2">
                  $1,500-$3,000
                </div>
                <p className="text-sm text-[#4a4a4a] mb-4">
                  per student annually
                </p>
                <p className="text-xs text-[#4a4a4a] leading-relaxed">
                  Intensive one-on-one or small group instruction requiring trained tutors, structured curriculum, and ongoing coordination.
                </p>
              </CardContent>
            </Card>

            {/* High-Cost Card 2 */}
            <Card className="bg-red-50/95 backdrop-blur-sm">
              <CardContent className="p-8 text-center">
                <div className="mb-4">
                  <AlertCircle className="w-8 h-8 text-[#b63435] mx-auto" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold text-[#2c5570] mb-3">
                  Literacy Curricula & Training
                </h3>
                <div className="text-4xl font-black text-[#b63435] mb-2">
                  $350-$600
                </div>
                <p className="text-sm text-[#4a4a4a] mb-4">
                  per student annually
                </p>
                <p className="text-xs text-[#4a4a4a] leading-relaxed">
                  Specialized reading programs, teacher professional development, and classroom materials requiring ongoing investment and training.
                </p>
              </CardContent>
            </Card>

            {/* THLP Card - Highlighted */}
            <Card className="border-4 border-[#fad53a] bg-gradient-to-br from-[#e8f5f7]/95 to-[#f0f8fa]/95 backdrop-blur-sm shadow-2xl transform hover:scale-105 transition-transform">
              <CardContent className="p-8 text-center">
                <div className="mb-4">
                  <CheckCircle className="w-8 h-8 text-[#14919f] mx-auto" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold text-[#14919f] mb-3">
                  The Home Library Project
                </h3>
                <div className="text-5xl font-black text-[#14919f] mb-2">
                  &lt;$50
                </div>
                <p className="text-sm text-[#4a4a4a] mb-4">
                  per student annually
                </p>
                <div className="pt-4 border-t border-[#14919f]/30">
                  <p className="text-xs text-[#2c5570] font-semibold space-y-1">
                    <span className="block">✓ Permanent assets (books stay in the home)</span>
                    <span className="block">✓ Compounding benefits over multiple years</span>
                    <span className="block">✓ Outcomes rival expensive interventions</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Paragraph below */}
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-lg leading-relaxed text-white mb-8">
              Each book is a permanent asset. Each year compounds growth. Book ownership builds the cognitive and emotional scaffolding that makes all classroom instruction stick.
            </p>
            
            {/* Donate CTA */}
            <Link to="/donate" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <Button
                size="lg"
                className="rounded-[50px] bg-[#fad53a] text-slate-900 shadow-lg hover:bg-[#fcc419] font-bold text-lg px-10 py-4"
                data-testid="donate-cta-research-roi"
              >
                Support This Research-Backed Approach
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 6: Book Ownership Is Prevention */}
      <section className="py-16 bg-[#e8f5f7]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl">
            <CardContent className="p-10">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-[#14919f]/10 flex items-center justify-center">
                    <BookOpen className="w-8 h-8 text-[#14919f]" strokeWidth={1.5} />
                  </div>
                </div>
                <div>
                  <h2 className="text-3xl font-extrabold text-[#2c5570] mb-6">
                    Book Ownership Is Prevention
                  </h2>
                  <p className="text-base leading-relaxed text-[#4a4a4a] mb-6">
                    Unlike remediation or costly interventions that begin after a child has already fallen behind, book ownership is a prevention-first strategy. It gives children the tools and experiences that make reading possible before gaps appear.
                  </p>
                  <p className="text-base leading-relaxed text-[#4a4a4a]">
                    By year five, as Delaware reaches 150,000 children with 1.5 million books annually, the state can demonstrate that universal book ownership is a financially sustainable, data-driven path to improving literacy at scale.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Section 7: Supported by a Century of Global Research */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-extrabold text-[#2c5570] mb-8 text-center">
            Supported by a Century of Global Research
          </h2>
          
          <Accordion type="single" collapsible className="w-full mx-auto">
            {researchStudies.map((study, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-lg font-semibold text-[#2c5570] hover:text-[#14919f] justify-center text-center">
                  {study.title}
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#4a4a4a] leading-relaxed text-center">
                  {study.content}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Closing Statement */}
          <div className="mt-12 text-center">
            <p className="text-xl leading-relaxed text-[#2c5570] font-bold">
              Across all studies, the conclusion is clear:
            </p>
            <p className="text-2xl font-black text-[#14919f] mt-2">
              Book ownership changes everything.
            </p>
          </div>
        </div>
      </section>

      {/* Section 8: CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-[#2c5570] to-[#14919f] text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-extrabold mb-6">
            Help Us Build Home Libraries Across Delaware
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Every donation provides books that change lives. Join us in transforming Delaware's literacy future.
          </p>
          <Link to="/donate" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <Button
              size="lg"
              className="rounded-[50px] bg-[#fad53a] text-slate-900 shadow-lg hover:bg-[#fcc419] font-bold text-xl px-10 py-6"
              data-testid="donate-cta-button"
            >
              Donate Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
